import { UniversalUpgradeProduct } from '@airship-platform/airship-proposal';
import {
  CameraIcon,
  ClipboardIcon,
  DollarSignIcon,
  DownloadIcon,
  OutageExperienceIcon,
  PencilIcon,
  PlusIcon,
  SurveyIcon,
} from '@airship-platform/jam-web/icons';
import { ChipProps } from '@mui/material/Chip';
import { CustomerMedia } from '../../../data-access/appointmentProposalFiles';
import { AppointmentProposalOption } from '../../../data-access/appointmentProposalOptions';
import { AppointmentProposal } from '../../../data-access/appointmentProposals';
import { AppointmentProposalUpgrade } from '../../../data-access/appointmentProposalUpgrades';
import { NavigationPath } from '../../../routes/paths';
import { NavigationAction } from '../models/proposalNavigation.models';

const TickPill: React.FC<{ text: string }> = ({ text }) => (
  <>&#10004; {text} </>
);

const getMediaPillProps = (assets: CustomerMedia[]): ChipProps => {
  const filteredAssets = assets.filter(
    (file) =>
      !file.metadata.uploadCategory ||
      ['EXISTING_SYSTEM', 'JOB_ATTACHMENT'].includes(
        file.metadata.uploadCategory
      )
  );
  return {
    label: filteredAssets.length ? (
      <TickPill text={`${filteredAssets.length} added`} />
    ) : (
      'Not started'
    ),
    color: filteredAssets.length ? 'softBlue' : undefined,
  };
};

const getNotesPillProps = (notes: AppointmentProposal['notes']): ChipProps => ({
  label: notes ? <TickPill text="Completed" /> : 'Not started',
  color: notes ? 'softBlue' : undefined,
});

const getHomeExperiencePillProps = (
  customerExperience: AppointmentProposal['customerExperience']
): ChipProps => {
  const itemsCount =
    customerExperience.current.length + customerExperience.priorities.length;

  return {
    label: itemsCount ? (
      <TickPill text={`${itemsCount} added`} />
    ) : (
      'Not started'
    ),
    color: itemsCount ? 'softBlue' : undefined,
  };
};

const getOutageExperiencePillProps = (
  outageExperience: AppointmentProposal['outageExperience']
): ChipProps => {
  const itemsCount =
    outageExperience.eventConcerns.length +
    outageExperience.homeAppliances.length +
    outageExperience.homeSystems.length +
    outageExperience.technologyElectronics.length;

  return {
    label: itemsCount ? (
      <TickPill text={`${itemsCount} added`} />
    ) : (
      'Not started'
    ),
    color: itemsCount ? 'softBlue' : undefined,
  };
};

const getSurveyPillProps = (
  surveyContacts: AppointmentProposal['surveyContacts']
): ChipProps => ({
  label: surveyContacts.length ? <TickPill text="Completed" /> : 'Not started',
  color: surveyContacts.length ? 'softBlue' : undefined,
});

const getFinancingPillProps = (
  options: AppointmentProposalOption[]
): ChipProps => {
  const financingPlansAdded = options.filter((o) => o.financingPlanId).length;
  const isCompleted = financingPlansAdded > 0;
  return {
    label: isCompleted ? (
      <TickPill text={`${financingPlansAdded} added`} />
    ) : (
      'Not started'
    ),
    color: isCompleted ? 'softBlue' : undefined,
  };
};

const getUpgradesPillProps = (
  proposal: AppointmentProposal,
  upgrades: UniversalUpgradeProduct[],
  proposalUpgrades: AppointmentProposalUpgrade[]
): ChipProps => {
  const selectedUpgradeIds = proposal.selectedUpgradeIds || [];
  const upgradesSelected =
    (selectedUpgradeIds.length ||
      upgrades.filter((upgrade) => {
        const matchesInstallationType = upgrade.installationTypes.length
          ? upgrade.installationTypes.includes(proposal.installationType)
          : true;
        const matchesProposalType = upgrade.proposalTypes.length
          ? upgrade.proposalTypes.includes(proposal.proposalType)
          : true;

        return matchesInstallationType && matchesProposalType;
      }).length) + proposalUpgrades.length;

  return {
    label: upgradesSelected ? (
      <TickPill text={`${upgradesSelected} added`} />
    ) : (
      'Not started'
    ),
    color: upgradesSelected ? 'softBlue' : undefined,
  };
};

const getDocumentsPillProps = (
  options: AppointmentProposalOption[]
): ChipProps => {
  const hasAcceptedOrSoldOption = options.some((option) =>
    ['ACCEPTED', 'SOLD'].includes(option.status)
  );
  return {
    label: hasAcceptedOrSoldOption ? 'Ready' : 'Not available',
    color: hasAcceptedOrSoldOption ? 'softBlue' : undefined,
  };
};

export const generateToolkitItems = (
  onNavigate: (
    path: NavigationPath,
    searchParams?: Record<string, string>
  ) => void,
  proposal: AppointmentProposal,
  assets: CustomerMedia[],
  options: AppointmentProposalOption[],
  upgrades: UniversalUpgradeProduct[],
  proposalUpgrades: AppointmentProposalUpgrade[]
): NavigationAction[] => [
  {
    label: 'Photos and videos',
    icon: <CameraIcon />,
    chipProps: getMediaPillProps(assets),
    onClick: () => onNavigate('capture-media'),
  },
  {
    label: 'Notes',
    icon: <PencilIcon />,
    chipProps: getNotesPillProps(proposal.notes),
    onClick: () => onNavigate('notes'),
  },
  {
    label: 'Home experience',
    icon: <ClipboardIcon />,
    chipProps: getHomeExperiencePillProps(proposal.customerExperience),
    onClick: () => onNavigate('customer-experience'),
  },
  {
    label: 'Outage experience',
    icon: <OutageExperienceIcon />,
    chipProps: getOutageExperiencePillProps(proposal.outageExperience),
    onClick: () => onNavigate('outage-experience'),
  },
  {
    label: 'Rebate survey',
    icon: <SurveyIcon />,
    chipProps: getSurveyPillProps(proposal.surveyContacts),
    onClick: () => onNavigate('send-survey'),
  },
  {
    label: 'Edit financing',
    icon: <DollarSignIcon />,
    chipProps: getFinancingPillProps(options),
    onClick: () => onNavigate('financing'),
  },
  {
    label: 'Edit upgrades',
    icon: <PlusIcon />,
    chipProps: getUpgradesPillProps(proposal, upgrades, proposalUpgrades),
    onClick: () => onNavigate('upgrades'),
  },
  {
    label: 'Documents',
    icon: <DownloadIcon />,
    chipProps: getDocumentsPillProps(options),
    onClick: () => onNavigate('documents'),
  },
];
